import React from 'react';
import { DepartmentHierarchyNodeFragmentFragment as DepartmentHierarchyNode } from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Chip, { ChipSizes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import { APP_URLS } from 'constants/urls';
import styles from './DepartmentHierarchyView.module.scss';

type Props = {
  node: DepartmentHierarchyNode;
  currentDepartmentId: string;
  expandedIds: Set<string>;
  loadingIds: Set<string>;
  childrenByParentId: Record<string, DepartmentHierarchyNode[]>;
  onToggle: (node: DepartmentHierarchyNode) => void;
};

export default function DepartmentHierarchyTreeNode({
  node,
  currentDepartmentId,
  expandedIds,
  loadingIds,
  childrenByParentId,
  onToggle,
}: Props) {
  const hasChildren = !!node.childrenCount;
  const isExpanded = expandedIds.has(node.id);
  const isLoading = loadingIds.has(node.id);
  const isCurrent = node.id === currentDepartmentId;
  const children = childrenByParentId[node.id];

  return (
    <li className={styles.node}>
      <div className={styles.nodeRow}>
        {hasChildren ? (
          <div className={styles.nodeRowToggleBtn}>
            <IconButton
              icon={isExpanded ? 'expand_more' : 'chevron_right'}
              type={IconButtonTypes.QuaternaryFilled}
              size={IconButtonSizes.ExtraSmall}
              onClick={() => onToggle(node)}
              disabled={isLoading}
            />
          </div>
        ) : (
          <span className={styles.togglePlaceholder} />
        )}
        <div className={styles.label}>
          <Chip
            label={node.name}
            leftIcon="circle"
            size={ChipSizes.small}
            type={colorToChipType(node.color)}
            asBlockElement
            fill={isCurrent}
            to={APP_URLS.dashboard.departments.view.getDynamicPath({
              pathParts: { id: node.id },
            })}
          />
        </div>
      </div>
      {hasChildren && isExpanded && (
        <ul className={styles.children}>
          {(isLoading && !children) || !hasChildren ? (
            <li className={styles.loading}>
              <CircleLoader
                size={16}
                padding="4px 0"
                classes={{ root: styles.loadingIconRoot }}
              />
            </li>
          ) : (
            (children || []).map((child) => (
              <DepartmentHierarchyTreeNode
                key={child.id}
                node={child}
                currentDepartmentId={currentDepartmentId}
                expandedIds={expandedIds}
                loadingIds={loadingIds}
                childrenByParentId={childrenByParentId}
                onToggle={onToggle}
              />
            ))
          )}
        </ul>
      )}
    </li>
  );
}
